import axios from 'axios';
import type { FormValues } from '../types';
import { templates } from '../data/templates';
import type { HistoryRecord, HistoryStatus } from '../../history/data/mockHistory';

const api = axios;

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['x-access-token'] = token;
  }
  return config;
});

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  id?: string | number;
  affectedRows?: number;
}

type BriefingPromocaoRow = Record<string, any>;

export interface BriefingProductValidationItem {
  ean: string;
  nomeProduto: string;
  nomeIndustria?: string;
  nomeMarca?: string;
  categoria?: string;
  grupo?: string;
  subGrupo?: string;
}

export interface BriefingProductValidationResult {
  requested: string[];
  found: BriefingProductValidationItem[];
  missing: string[];
}

const LIST_FIELDS = new Set([
  'sellersIncluidos',
  'sellersExcecao',
  'cnpjsIncluidos',
  'cnpjsExcecao',
  'canais',
  'tipoPedido',
  'itensIncluidos',
  'itensExcluidos',
  'eansInclusao',
  'eansExclusao',
  'skusInclusao',
  'skusExclusao',
  'marcasInclusao',
  'marcasExclusao',
  'categoriasInclusao',
  'categoriasExclusao',
  'subcategoriasInclusao',
  'subcategoriasExclusao',
  'departamentosInclusao',
  'departamentosExclusao',
  'industriasInclusao',
  'industriasExclusao',
  'familiasInclusao',
  'familiasExclusao',
  'eansObrigatorios',
  'skusObrigatorios',
]);

const CHILD_COUNT_KEY = 'qtdCardsFilhos';
const CHILD_FIELD_SUFFIX_RE = /__child(\d+)$/;
const CHILD_MAX_COUNT = 4;
const CHILD_CARD_TEMPLATE_IDS = new Set(['t3', 't4a', 't4b']);
const CHILD_SPECIFIC_KEYS = new Set([
  'numeroCard',
  'tipoDesconto',
  'valorDesconto',
  'criterioDesconto',
  'qtdMinima',
  'qtdMaxima',
  'valorMinimo',
  'valorMaximo',
  'tipoItemIncluido',
  'itensIncluidos',
  'tipoItemExcluido',
  'itensExcluidos',
  'eansInclusao',
  'eansExclusao',
  'skusInclusao',
  'skusExclusao',
  'marcasInclusao',
  'marcasExclusao',
  'categoriasInclusao',
  'categoriasExclusao',
  'subcategoriasInclusao',
  'subcategoriasExclusao',
  'departamentosInclusao',
  'departamentosExclusao',
  'industriasInclusao',
  'industriasExclusao',
  'familiasInclusao',
  'familiasExclusao',
  'eansObrigatorios',
  'skusObrigatorios',
  'skuCombo',
  'tipoFamilia',
  'nomeFamilia',
  'industriaFamilia',
  'obsFamilia',
  'skusFamilia',
  'qtdEANsDistintos',
  'qtdEansDistintos',
  'qtdMinimaAtivacao',
  'qtdMinAtivacao',
  'qtdMaximaAtivacao',
  'qtdMaxAtivacao',
  'qtdFamiliasAtivacao',
  'familiaObrigatoria',
  'posicaoFamilia',
  'posicaoFamiliaLista',
  'descontoFamilia',
  'posicaoLista',
  'posicaoListaSecao',
  'secaoLista',
]);

const COLUMN_TO_FORM_KEY: Record<string, string> = {
  briefing_group_id: 'briefingGroupId',
  parent_briefing_id: 'parentBriefingId',
  template_id: 'templateId',
  tipo_solicitacao: 'tipoSolicitacao',
  id_ifc: 'idIFC',
  tipo_mecanica: 'tipoMecanica',
  tipo_lista: 'tipoLista',
  recompra_disponivel: 'recompraDisponivel',
  id_lista_original: 'idListaOriginal',
  nome_promo: 'nomePromo',
  nome_exibicao: 'nomeExibicao',
  descricao: 'descricao',
  descricao_interna: 'descricaoInterna',
  tipo_desconto: 'tipoDesconto',
  valor_desconto: 'valorDesconto',
  industria: 'industria',
  multi_patrocinador: 'multiPatrocinador',
  patrocinadores: 'patrocinadores',
  cumulativa: 'cumulativa',
  inicio_vigencia: 'inicioVigencia',
  fim_vigencia: 'fimVigencia',
  budget_atrelado: 'budgetAtrelado',
  budget_valor: 'budgetValor',
  sellers_incluidos: 'sellersIncluidos',
  sellers_excecao: 'sellersExcecao',
  cnpjs_incluidos: 'cnpjsIncluidos',
  cnpjs_excecao: 'cnpjsExcecao',
  canais: 'canais',
  tipo_pedido: 'tipoPedido',
  criterio_desconto: 'criterioDesconto',
  qtd_minima: 'qtdMinima',
  qtd_maxima: 'qtdMaxima',
  valor_minimo: 'valorMinimo',
  valor_maximo: 'valorMaximo',
  numero_card: 'numeroCard',
  tipo_item_incluido: 'tipoItemIncluido',
  itens_incluidos: 'itensIncluidos',
  tipo_item_excluido: 'tipoItemExcluido',
  itens_excluidos: 'itensExcluidos',
  eans_inclusao: 'eansInclusao',
  eans_exclusao: 'eansExclusao',
  skus_inclusao: 'skusInclusao',
  skus_exclusao: 'skusExclusao',
  marcas_inclusao: 'marcasInclusao',
  marcas_exclusao: 'marcasExclusao',
  categorias_inclusao: 'categoriasInclusao',
  categorias_exclusao: 'categoriasExclusao',
  subcategorias_inclusao: 'subcategoriasInclusao',
  subcategorias_exclusao: 'subcategoriasExclusao',
  departamentos_inclusao: 'departamentosInclusao',
  departamentos_exclusao: 'departamentosExclusao',
  industrias_inclusao: 'industriasInclusao',
  industrias_exclusao: 'industriasExclusao',
  familias_inclusao: 'familiasInclusao',
  familias_exclusao: 'familiasExclusao',
  eans_obrigatorios: 'eansObrigatorios',
  skus_obrigatorios: 'skusObrigatorios',
  sku_combo: 'skuCombo',
  tipo_familia: 'tipoFamilia',
  nome_familia: 'nomeFamilia',
  industria_familia: 'industriaFamilia',
  obs_familia_existente: 'obsFamilia',
  skus_familia: 'skusFamilia',
  qtd_eans_distintos: 'qtdEANsDistintos',
  qtd_min_ativacao: 'qtdMinimaAtivacao',
  qtd_max_ativacao: 'qtdMaximaAtivacao',
  qtd_familias_ativacao: 'qtdFamiliasAtivacao',
  familia_obrigatoria: 'familiaObrigatoria',
  posicao_familia_lista: 'posicaoFamilia',
  desconto_familia: 'descontoFamilia',
  posicao_lista_secao: 'posicaoLista',
  secao_lista: 'secaoLista',
  cupom: 'cupom',
  codigo_cupom: 'codigoCupom',
  qtd_cupons_disponiveis: 'qtdCupons',
  qtd_usos_por_cliente: 'usosPorCliente',
};

function handleResponse<T>(res: { data: ApiResponse<T> }): T {
  if (!res.data.success) throw new Error(res.data.error || 'API Error');
  return res.data.data;
}

function handleMutationResponse<T = any>(res: { data: ApiResponse<T> }): ApiResponse<T> {
  if (!res.data.success) throw new Error(res.data.error || 'API Error');
  return res.data;
}

function splitList(value: any): string[] {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (value === null || value === undefined || value === '') return [];

  const str = String(value).trim();
  if (!str) return [];

  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
  } catch (_) {
    // O backend atual grava listas separadas por vírgula.
  }

  return str
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatDateTimeToForm(value: any): string {
  if (!value) return '';

  if (typeof value === 'string') {
    const brMatch = value.match(/^\d{2}\/\d{2}\/\d{4}\s+\d{2}:\d{2}/);
    if (brMatch) return value.slice(0, 16);

    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (isoMatch) {
      const [, yyyy, mm, dd, hh, min] = isoMatch;
      return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formDateToIsoLike(value: any): string {
  if (!value) return '';

  if (typeof value === 'string') {
    const brMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{2}):(\d{2})/);
    if (brMatch) {
      const [, dd, mm, yyyy, hh, min] = brMatch;
      return `${yyyy}-${mm}-${dd}T${hh}:${min}:00`;
    }

    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
    if (isoMatch) return value.replace(' ', 'T');
  }

  return String(value);
}

function normalizeScalar(value: any): string {
  if (value === null || value === undefined) return '';
  return String(value);
}

function normalizeChildCount(value: unknown): number {
  if (Array.isArray(value)) return 0;
  const parsed = parseInt(String(value ?? '').replace(/[^0-9]/g, ''), 10);
  if (Number.isNaN(parsed)) return 0;
  return Math.max(0, Math.min(CHILD_MAX_COUNT, parsed));
}

function createBriefingGroupId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `briefing-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getChildFieldInfo(key: string): { baseKey: string; childNumber: number } | null {
  const match = key.match(CHILD_FIELD_SUFFIX_RE);
  if (!match) return null;

  const childNumber = parseInt(match[1], 10);
  if (Number.isNaN(childNumber)) return null;

  return {
    baseKey: key.replace(CHILD_FIELD_SUFFIX_RE, ''),
    childNumber,
  };
}

function buildCardPayloads(templateId: string, values: FormValues): FormValues[] {
  const parentPayload: FormValues = {};

  Object.entries(values).forEach(([key, value]) => {
    if (key === CHILD_COUNT_KEY || getChildFieldInfo(key)) return;
    parentPayload[key] = value;
  });

  const childCount = CHILD_CARD_TEMPLATE_IDS.has(templateId) && parentPayload.numeroCard === '1'
    ? normalizeChildCount(values[CHILD_COUNT_KEY])
    : 0;

  if (childCount <= 0) return [parentPayload];

  const childPayloads: FormValues[] = [];

  for (let childNumber = 2; childNumber <= childCount + 1; childNumber += 1) {
    const childPayload: FormValues = {
      ...parentPayload,
      numeroCard: String(childNumber),
    };

    CHILD_SPECIFIC_KEYS.forEach((key) => {
      if (key !== 'numeroCard') delete childPayload[key];
    });

    Object.entries(values).forEach(([key, value]) => {
      const childInfo = getChildFieldInfo(key);
      if (!childInfo || childInfo.childNumber !== childNumber) return;
      childPayload[childInfo.baseKey] = value;
    });

    childPayload.numeroCard = String(childNumber);
    childPayloads.push(childPayload);
  }

  return [parentPayload, ...childPayloads];
}

async function postBriefingPromocao(templateId: string, values: FormValues): Promise<string | number | undefined> {
  const res = await api.post('/briefing-promocoes', {
    ...values,
    templateId,
  });
  return handleMutationResponse(res).id;
}

function mapBriefingRowToValues(row: BriefingPromocaoRow): FormValues {
  const values: FormValues = {};

  Object.entries(COLUMN_TO_FORM_KEY).forEach(([column, key]) => {
    const raw = row[column];
    if (raw === undefined || raw === null) return;

    if (key === 'inicioVigencia' || key === 'fimVigencia') {
      values[key] = formatDateTimeToForm(raw);
    } else if (LIST_FIELDS.has(key)) {
      values[key] = splitList(raw);
    } else {
      values[key] = normalizeScalar(raw);
    }
  });

  return values;
}

function childFieldKey(fieldKey: string, childNumber: number): string {
  return `${fieldKey}__child${childNumber}`;
}

function mergeChildRowsIntoValues(parentValues: FormValues, childValuesList: FormValues[]): FormValues {
  if (!childValuesList.length) return parentValues;

  const values: FormValues = { ...parentValues };
  const childNumbers: number[] = [];

  childValuesList.forEach((childValues) => {
    const childNumber = parseInt(String(childValues.numeroCard ?? '').replace(/[^0-9]/g, ''), 10);
    if (Number.isNaN(childNumber) || childNumber < 2) return;

    childNumbers.push(childNumber);
    values[childFieldKey('numeroCard', childNumber)] = String(childNumber);

    Object.entries(childValues).forEach(([key, value]) => {
      if (key === 'numeroCard') return;
      if (key === 'templateId' || key === 'briefingGroupId' || key === 'parentBriefingId') return;
      values[childFieldKey(key, childNumber)] = value;
    });
  });

  if (childNumbers.length) {
    values[CHILD_COUNT_KEY] = String(Math.min(CHILD_MAX_COUNT, Math.max(...childNumbers) - 1));
  }

  return values;
}

function getTemplateId(row: BriefingPromocaoRow, values: FormValues): string {
  const explicit = (values.templateId as string) || row.template_id;
  if (explicit && templates.some((template) => template.id === explicit)) return explicit;

  const tipoMecanica = String(values.tipoMecanica || row.tipo_mecanica || '');
  const tipoLista = String(values.tipoLista || row.tipo_lista || '');
  const cupom = String(values.cupom || row.cupom || '');

  if (tipoMecanica === '2') return 't2';
  if (tipoMecanica === '3') return 't3';
  if (tipoMecanica === '4') return tipoLista === '2' ? 't4b' : 't4a';
  if (tipoMecanica === '5') return 't5';
  if (tipoMecanica === '6') return 't6';
  if (tipoMecanica === '7') return 't7';
  if (cupom === '2') return 't8';

  return 't1';
}

function getStatus(values: FormValues): HistoryStatus {
  const startValue = formDateToIsoLike(values.inicioVigencia);
  const endValue = formDateToIsoLike(values.fimVigencia);

  if (!startValue || !endValue) return 'rascunho';

  const now = new Date();
  const start = new Date(startValue);
  const end = new Date(endValue);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 'rascunho';
  if (now < start) return 'agendado';
  if (now > end) return 'encerrado';

  return 'ativo';
}

export function mapBriefingRowToHistoryRecord(row: BriefingPromocaoRow): HistoryRecord {
  let values = mapBriefingRowToValues(row);
  const templateId = getTemplateId(row, values);

  if (CHILD_CARD_TEMPLATE_IDS.has(templateId) && Array.isArray(row.children)) {
    values = mergeChildRowsIntoValues(values, row.children.map(mapBriefingRowToValues));
  }

  values.templateId = templateId;

  const template = templates.find((item) => item.id === templateId) || templates[0];

  return {
    id: String(row.id),
    briefingGroupId: row.briefing_group_id ? String(row.briefing_group_id) : undefined,
    parentBriefingId: row.parent_briefing_id ? String(row.parent_briefing_id) : undefined,
    templateId,
    templateName: template?.name || 'Template não identificado',
    templateColor: template?.color || '#1976d2',
    templateNumber: template?.number || '—',
    createdAt: row.criado_em || row.created_at || '',
    updatedAt: row.atualizado_em || row.updated_at || row.criado_em || '',
    status: getStatus(values),
    author: row.criado_por_nome || row.atualizado_por_nome || '—',
    values,
  };
}

export async function fetchBriefingPromocoes(params?: Record<string, string | number | undefined>): Promise<HistoryRecord[]> {
  const res = await api.get('/briefing-promocoes', { params });
  const rows = handleResponse<BriefingPromocaoRow[]>(res);
  return rows.map(mapBriefingRowToHistoryRecord);
}

export async function fetchBriefingPromocao(id: string | number): Promise<HistoryRecord | null> {
  const res = await api.get(`/briefing-promocoes/${id}`);
  const row = handleResponse<BriefingPromocaoRow | null>(res);
  return row ? mapBriefingRowToHistoryRecord(row) : null;
}

export async function createBriefingPromocao(templateId: string, values: FormValues): Promise<string | number | undefined> {
  const payloads = buildCardPayloads(templateId, values);
  const briefingGroupId = payloads.length > 1 ? createBriefingGroupId() : undefined;
  let parentId: string | number | undefined;

  for (let index = 0; index < payloads.length; index += 1) {
    const payload = { ...payloads[index] };

    if (briefingGroupId) {
      payload.briefingGroupId = briefingGroupId;
    }

    if (index > 0) {
      if (parentId === undefined || parentId === null || parentId === '') {
        throw new Error('Não foi possível vincular os cards filhos ao briefing pai.');
      }

      payload.parentBriefingId = String(parentId);
    }

    const id = await postBriefingPromocao(templateId, payload);
    if (parentId === undefined) parentId = id;
  }

  return parentId;
}

export async function updateBriefingPromocao(id: string | number, templateId: string, values: FormValues): Promise<boolean> {
  const res = await api.put(`/briefing-promocoes/${id}`, {
    ...values,
    templateId,
  });
  handleMutationResponse(res);
  return true;
}

export async function deleteBriefingPromocao(id: string | number): Promise<boolean> {
  const res = await api.delete(`/briefing-promocoes/${id}`);
  handleMutationResponse(res);
  return true;
}

export async function validateBriefingProducts(items: string[]): Promise<BriefingProductValidationResult> {
  const uniqueItems = Array.from(
    new Set(items.map((item) => String(item ?? '').trim()).filter(Boolean))
  );

  if (uniqueItems.length === 0) {
    return {
      requested: [],
      found: [],
      missing: [],
    };
  }

  const res = await api.post('/briefing-promocoes/produtos/validar', {
    items: uniqueItems,
  });

  return handleResponse<BriefingProductValidationResult>(res);
}


export interface BriefingPromocoesAgentResponse {
  message: string;
  formValues: FormValues;
  fields: FormValues;
  templateId: string | null;
  missingFields: string[];
  shouldFill: boolean;
}

export async function sendBriefingPromocoesAgentMessage(params: {
  chatId: string;
  message: string;
  templateId: string;
  templateName?: string;
  currentValues: FormValues;
}): Promise<BriefingPromocoesAgentResponse> {
  const res = await api.post<ApiResponse<BriefingPromocoesAgentResponse>>(
    '/briefing-promocoes-agent/chat',
    params
  );

  if (!res.data.success) {
    throw new Error(res.data.error || 'Erro ao conversar com o agente.');
  }

  return res.data.data;
}
